import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import SlideTransition from 'Shared/components/transitions/slide-transition';
import FadeTransition from 'Shared/components/transitions/fade-transition';
import { useTranslation } from 'Shared/util/custom-hooks';
import { funcFilterStatus } from 'Shared/util/translations';
import { isActiveItem } from './menu-utils';
import styles from './mobile-menu.css';


const renderSubItem = (
  item,
  isActive,
) => (
  item.link ? (
    <Link
      to={item.link}
      className={`${styles.subItem} ${isActive ? styles.active : ''}`}
      key={`${item.text};${item.link}`}
    >
      {item.text}
    </Link>
  ) : (
    <span
      className={`${styles.subItem} ${isActive ? styles.active : ''}`}
      key={`${item.text};${item.link}`}
    >
      {item.text}
    </span>
  )
);


const MenuItem = ({
  showMenu,
  item,
  currentPage,
  index,
  totalItems,
}) => {
  const [showSubMenu, setShowSubMenu] = useState(false);
  const isActive = isActiveItem(item, currentPage);
  const t = useTranslation(x => x.menuItems);

  useEffect(() => {
    setShowSubMenu(isActive && (item.subItems && item.subItems.length > 0));
  }, [showMenu]);

  const enterDuration = 300;
  const exitDuration = 100;
  const enterDelay = (50 + (index * 20));
  const exitDelay = ((totalItems - 1 - index) * 20);

  return (
    <FadeTransition
      isOpen={showMenu}
      className={styles.itemContainer}
      enterDuration={enterDuration}
      exitDuration={exitDuration}
      enterDelay={enterDelay}
      exitDelay={exitDelay}
    >
      {item.link ? (
        <Link
          to={item.link}
          className={`${styles.item} ${isActive ? styles.active : ''}`}
        >
          {item.text}
        </Link>
      ) : (
        <span
          className={styles.item}
          onClick={() => setShowSubMenu(!showSubMenu)}
        >
          {item.text}
        </span>
      )}
      {item.subItems && (
        <SlideTransition
          isOpen={showSubMenu}
          enterDuration={300}
          exitDuration={300}
        >
          {item.subItems.map((subItem) => {
            const translatedSubItem = funcFilterStatus(subItem, t, 'text');
            return renderSubItem(translatedSubItem, isActiveItem(translatedSubItem, currentPage));
          })}
        </SlideTransition>
      )}
    </FadeTransition>
  );
};

MenuItem.propTypes = {
  showMenu: PropTypes.bool.isRequired,
  item: PropTypes.object.isRequired,
  currentPage: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
};


const MobileMenu = ({
  showMenu,
  items,
  currentPage,
  pageHasChanged,
}) => {
  const itemsContainerRef = useRef();
  const t = useTranslation(x => x.menuItems);

  useEffect(() => {
    if (showMenu) {
      disableBodyScroll(itemsContainerRef.current);
    } else {
      enableBodyScroll(itemsContainerRef.current);
    }

    return () => enableBodyScroll(itemsContainerRef.current);
  }, [showMenu]);

  const exitDuration = (pageHasChanged ? 1 : 400);
  const exitDelay = (pageHasChanged ? 100 : 0);

  return (
    <SlideTransition
      ref={itemsContainerRef}
      isOpen={showMenu}
      enterDuration={560}
      exitDuration={exitDuration}
      exitDelay={exitDelay}
      className={styles.itemsContainer}
      maxHeight="calc(100vh - 122px)"
    >
      {items.map((item, index) => {
        const translatedItem = funcFilterStatus(item, t, 'text');
        return (
          <MenuItem
            key={`${item.text};${item.link}`}
            showMenu={showMenu}
            item={translatedItem}
            currentPage={currentPage}
            index={index}
            totalItems={items.length}
          />
        );
      })}
    </SlideTransition>
  );
};

MobileMenu.propTypes = {
  showMenu: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string.isRequired,
    link: PropTypes.string,
    subItems: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string.isRequired,
      link: PropTypes.string.isRequired,
    })),
  })).isRequired,
  currentPage: PropTypes.string.isRequired,
  pageHasChanged: PropTypes.bool.isRequired,
};

export default MobileMenu;
